import { useEffect } from "react";
import {
  useIsRestoring,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

const REVALIDATE_ROOTS = [["portal"], ["building"]];
/** Under the 30-min STALE_TRACKER_DEFAULT, so focus refetch alone misses it. */
const HIDDEN_REVALIDATE_MS = 5 * 60_000;

let bootRevalidated = false;

/**
 * Active portal queries refetch; inactive ones refetch when next mounted.
 * cancelRefetch:false keeps a stale-on-mount fetch already in flight; the
 * default cancels and reissues it, so the server computes it twice.
 */
export const revalidatePortal = (qc: QueryClient) =>
  REVALIDATE_ROOTS.forEach((queryKey) => {
    qc.invalidateQueries(
      { queryKey, refetchType: "active" },
      { cancelRefetch: false },
    );
  });

/** Any portal or building page data in the cache, e.g. restored from localStorage. */
export const hasPortalPageData = (qc: QueryClient) =>
  REVALIDATE_ROOTS.some((queryKey) =>
    qc
      .getQueryCache()
      .findAll({ queryKey })
      .some((q) => q.state.data !== undefined),
  );

/**
 * Persisted entries restore with their old dataUpdatedAt and read as fresh, so
 * revalidate once per page load and after the tab was hidden >= 5 min.
 */
export const usePortalRevalidate = () => {
  const qc = useQueryClient();
  // An invalidate sent before hydrate finishes is overwritten by the restore.
  const isRestoring = useIsRestoring();

  useEffect(() => {
    if (isRestoring || bootRevalidated) return;
    bootRevalidated = true;
    revalidatePortal(qc);
  }, [qc, isRestoring]);

  useEffect(() => {
    let hiddenAt: number | null = null;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt !== null && Date.now() - hiddenAt >= HIDDEN_REVALIDATE_MS) {
        revalidatePortal(qc);
      }
      hiddenAt = null;
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [qc]);
};

/** Tests only. */
export const resetPortalRevalidateForTests = () => {
  bootRevalidated = false;
};
